import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { DateInput } from "@/components/ui/date-input"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import type { PageProps } from "@/types/inertia"
import type { TaskRecord } from "@/components/tasks/types"

type TaskFormDialogProps = {
  readonly trigger: React.ReactNode
  readonly task?: TaskRecord
  readonly contactId?: number
  readonly clientId?: number
}

export function TaskFormDialog({
  trigger,
  task,
  contactId,
  clientId,
}: TaskFormDialogProps) {
  const { props } = usePage<PageProps>()
  const [open, setOpen] = React.useState(false)
  const [title, setTitle] = React.useState(task?.title ?? "")
  const [notes, setNotes] = React.useState(task?.notes ?? "")
  const [dueOn, setDueOn] = React.useState(task?.due_on?.slice(0, 10) ?? "")
  const [processing, setProcessing] = React.useState(false)
  const errors = props.errors

  React.useEffect(() => {
    if (open) {
      setTitle(task?.title ?? "")
      setNotes(task?.notes ?? "")
      setDueOn(task?.due_on?.slice(0, 10) ?? "")
    }
  }, [open, task])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const payload = {
      task: {
        title,
        notes,
        due_on: dueOn || null,
        contact_id: task?.contact_id ?? contactId ?? null,
        client_id: task?.client_id ?? clientId ?? null,
      },
    }
    const options = {
      preserveScroll: true,
      onStart: () => setProcessing(true),
      onFinish: () => setProcessing(false),
      onSuccess: () => setOpen(false),
    }
    if (task) {
      router.patch(`/tasks/${task.id}`, payload, options)
    } else {
      router.post("/tasks", payload, options)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{task ? "Edit task" : "New task"}</DialogTitle>
          <DialogDescription>
            {task ? "Update the task details below." : "Add a task to track follow-up work."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="task-title">Title</label>
            <Input
              id="task-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
            {errors.title && (
              <p className="text-xs text-danger-display">{errors.title}</p>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="task-notes">Notes</label>
            <textarea
              id="task-notes"
              className="form-control form-control-textarea min-h-24"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
            {errors.notes && (
              <p className="text-xs text-danger-display">{errors.notes}</p>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="task-due-on">Due date</label>
            <DateInput
              id="task-due-on"
              value={dueOn}
              onChange={(event) => setDueOn(event.target.value)}
            />
            {errors.due_on && (
              <p className="text-xs text-danger-display">{errors.due_on}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {task ? "Save task" : "Add task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
